export type UserMetadataDto = {
  emailVerified?: boolean;
  shouldChangePassword?: boolean;
  twoFactorType?: 'email' | 'appAuth';

  applicationsMetadata?: {
    superpay23: {
      defaultBankAccount?: string;
      hiddeBalanceAlways?: boolean;
      receivePaymentsNotifications?: boolean;
      /** Incluir monto y remitente en el correo de pago recibido (por defecto no). */
      paymentEmailIncludeDetails?: boolean;
      autoCloseSession?: boolean;
      /** Autorizar transferencias con huella (WebAuthn) en vez del OTP. */
      biometricForTransactions?: boolean;
    };
  };
};

export type AppConfig = {
  business_min_wallet_balance: number;
  under_maintenance: boolean;
  allow_internal_transfers: boolean;
  allow_extraction_requests: boolean;
  allow_bank_extraction_requests: boolean;
  visa_mastercard_funding_fee_percent: number;
  card_recharge_suspended: boolean;
  // % de comisión aplicado al precio de las recargas móviles (config de cliente).
  recharge_fee_percent?: number;
  // Cuando es true, las recargas telefónicas están temporalmente suspendidas.
  recharge_phone_suspended?: boolean;
  max_funding_amount?: number | string;
  force_user_personal_info?: boolean;
  funding_support_number?: string;
  enable_kyc_verification?: boolean;
  // 'all' o 'selected'. La LISTA de correos habilitados ya no se publica aquí:
  // vive en `kyc_verification_allowed_emails`, una config de sistema que no es
  // de cliente, y el backend responde por el usuario del token en
  // v1/auth/feature-flags.
  kyc_verification_enabled_for?: 'all' | 'selected';
  // Cuando es true, todas las operaciones de extracción (transferencia interna,
  // pagos a tarjetas/billetera, pagos bancarios, pago de servicios y pago por
  // merchant) requieren KYC aprobado. Misma key que en el API Gateway.
  kyc_required_for_extractions?: boolean;
  // Cuando es true, el KYC es obligatorio a partir de la SEGUNDA operación de
  // extracción: la primera exitosa marca customer.metadata.firstExtractionAt y
  // los siguientes intentos se bloquean hasta aprobar KYC. Misma key que en el
  // API Gateway.
  kyc_required_after_first_extraction?: boolean;
  // Interruptor general de LogRocket.
  enable_logrocket?: boolean;
  // A quién se graba: 'all' (todas las sesiones) o 'selected' (solo los correos
  // habilitados). La LISTA de correos no se publica aquí: vive en la config
  // interna cifrada del API Gateway (LOGROCKET_ALLOWED_EMAILS) y el backend
  // responde por el usuario del token en v1/auth/session-recording.
  logrocket_enabled_for?: 'all' | 'selected';
};

export type ExtractionRequest = {
  id: string;
  sequentialNumber?: number;
  paymentRequestNumber?: string;
  status: ExtractionRequestStatus;
  account_number: string;
  amount: number;
  beneficiary_id: string;
  reservation_reference: string;
  beneficiary: Beneficiary;
  createdAt: string;
  /**
   * Comisión congelada al crear la solicitud. Sólo la llevan las creadas con la
   * comisión POR ENCIMA del importe (`ADDED`), donde `reservedAmount` —lo retenido de
   * la cuenta— es el importe más la comisión.
   */
  feeApplicationMode?: 'INCLUDED' | 'ADDED';
  feePercent?: number | null;
  feeAmount?: number;
  reservedAmount?: number;
};

export enum ExtractionRequestStatus {
  REGISTRADA = 'Registrada',
  ORDENADA_A_PAGAR = 'Ordenada a Pagar',
  PAGADA = 'Pagada',
  CANCELADA = 'Cancelada',
  // An advance payment the external service cancelled; the money went back to the account.
  DEVUELTA = 'Devuelta'
}

export type Beneficiary = {
  id: string;
  bank_account?: {
    accountNumber: string;
    currency: {
      code: string;
      symbol: string;
    };
  };
  alias: string;
  account_beneficiary: string;
  is_internal: boolean;
  account_number: string;
  service_id: string;
  serviceCatalog: ServiceCatalog;
  active: boolean;
};

export type BankBeneficiary = {
  _id: string;
  companyId: string;
  bankId: string;
  accountNumber: string;
  currencyId: string;
  alias: string;
  iban?: string;
  active: boolean;
  company?: {
    _id: string;
    name: string;
    cif: string;
    address?: string;
  };
  bank?: {
    _id: string;
    name: string;
    code?: string;
    country?: string;
  };
  currency?: {
    _id: string;
    code: string;
    name: string;
    symbol: string;
  };
};
export enum ServiceType {
  BANK = 'BANK',
  WALLET = 'WALLET',
  CREDIT_CARD = 'CREDIT_CARD',
  CASH = 'CASH',
  TROPIPAY = 'TROPIPAY',
  EPCARD = 'EPCARD'
}

export enum ServiceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE'
}

export type ServiceCatalog = {
  name: string;
  description: string;
  type: ServiceType;
  status: ServiceStatus;
};

export type ExchangeRate = {
  convertedAmount: number;
  sourceRate: string | null;
  targetRate: string | null;
  conversionRate: string | null;
  baseCurrencyCode: string;
};

export enum OtpReason {
  REGISTER = 'register',
  LOGIN = 'login',
  FORGOT_PASSWORD = 'forgot_password',
  SET_TWO_FA_EMAIL = 'set_two_fa_email',
  SET_TWO_FA_APP = 'set_two_fa_app',
  PAYMENT = 'payment',
  VERIFY_EMAIL = 'verify_email'
}

export enum QRPaymentEnum {
  GENERATED_QR = 'generated_qr',
  ACCOUNT_QR = 'account_qr'
}

export enum QrPaymentRequestStatus {
  canceled_by_user = 'canceled_by_user',
  scanned = 'scanned',
  rejected = 'rejected',
  pending = 'pending',
  approved = 'approved',
  success = 'success'
}
export class LoginDto {
  email: string;
  password: string;
  requestId?: number;
  captchaToken?: string; // M-08 Fase 5 — token Turnstile (solo tras N fallos)
}

export type PaginationDto = {
  page: number;
  limit: number;
};

export type Pagination = {
  totalElements: number;
  hasNextPage: boolean;
  nextPage: number;
  previousPage: number;
  lastPage: number;
};

export type GetManyResponse<T = any> = {
  elements: T[];
  pagination: Pagination;
};

export type Base = {
  _id: string;
  _deleted: boolean;
  createdAt: string;
  updatedAt: string;
  id: string;
  tenantId: string;
  createdBy: string;
  updatedBy: string;
};

export type User = Base & {
  email: string;
  account_status: string;
  first_name: string;
  last_name: string;
  tenantId: string;
  metadata: Record<string, any> & UserMetadataDto;
  roles: string[];
  permissions: string[];
};

export type Person = Base & {
  firstName: string;
  lastName: string;
  birthCountry: any;
  occupation: any;
  personKyc: any;
};

export enum CustomerType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS'
}

export enum CustomerCategory {
  STANDARD = 'STANDARD',
  PREMIUM = 'PREMIUM',
  VIP = 'VIP'
}

export enum CustomerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  BLOCKED = 'BLOCKED',
  PENDING = 'PENDING'
}

export enum KycStatus {
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  ADDITIONAL_INFO_REQUIRED = 'ADDITIONAL_INFO_REQUIRED',
  EXPIRED = 'EXPIRED'
}

export type Customer = Base & {
  email: string;
  application_id: string;
  customerType: CustomerType;
  customerCategory: CustomerCategory;
  status: CustomerStatus;
  kycStatus: KycStatus;
  userId: string;
  personId: string;
  isLegacy: boolean;
  legacyMetadata: Record<string, any>;
  metadata: Record<string, any>;
  person: Person;
  business: Business;
};

export type Business = Base & {
  businessName: string;
  taxId: string;
  // Estado representativo del KYC de la empresa (lo sincroniza el Core).
  businessKycStatus?: KycStatus | string;
};

export type Currency = {
  id: number;
  code: string;
  name: string;
  symbol: string;
  isBaseCurrency: boolean;
  sequentialNumber: number;
};
export enum BankAccountStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  BLOCKED = 'BLOCKED',
  PENDING = 'PENDING',
  CLOSED = 'CLOSED',
  DISABLED = 'DISABLED',
  FROZEN = 'FROZEN'
}

export type AccountAccess = {
  _id: string;
  _deleted: boolean;
  createdBy: string;
  accountNumber: string;
  customerId: string;
  authorizedUserId: string;
  userEmail: string;
  userFullName: string;
  permission: string;
  actionsAllowed: string[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  id: string;
};

export enum OwnerType {
  PERSONAL = 'PERSONAL',
  BUSINESS = 'BUSINESS',
  JOINT = 'JOINT',
  TRUST = 'TRUST',
  GOVERNMENT = 'GOVERNMENT'
}

export type BankAccount = Base & {
  id: number;
  reservedBalance: number;
  balance: number;
  availableBalance: number;
  accountNumber: string;
  customerId: string;
  currencyId: number;
  status: BankAccountStatus;
  verified: boolean;
  dailyTransactionLimit: number;
  transactionLimit: number;
  openDate: string;
  closeDate: string;
  accountOfficer: string;
  accountTypeId: number;
  accountType: {
    name: string;
    key: string;
  };
  sequentialNumber: number;
  application_id: string;
  suspensionReason: string;
  suspensionDate: string;
  reactivationDate: string;
  lastTransactionDate: string;
  description: string;
  accountSegment: string;
  externalAccountId: string;
  isLegacy: boolean;
  legacyId: string;
  currency: Currency;
  // Propiedades para cuentas autorizadas
  authorizedAccount?: boolean;
  ownAccount?: boolean;
  customer?: Customer;
  access?: AccountAccess;
  // Etiquetas de la cuenta (nomenclador bank-account-label del core). Array de
  // nombres. Se usa, p.ej., para detectar cuentas "merchant".
  labels?: string[];
  // Metadata con información del origen del sistema
  metadata?: {
    origin?: 'Ecotreew' | 'Provtreew';
  };
  // Servicios accesibles precargados desde auth/me y auth/login
  accessibleServices?: { _id: string; key: string; name: string; status: string }[];
};

export type LoginResponse = {
  token: string;
  refreshToken: string;
  user: User;
  customer: Customer;
  bankAccounts: BankAccount[];
};

export type ClientFromToken = {
  user: User;
  customer: Customer;
  bankAccounts: BankAccount[];
};

export enum PaymentRequestStatus {
  pending = 'pending',
  rejected = 'rejected',
  approved = 'approved',
  success = 'success',
  canceled_by_user = 'canceled_by_user'
}

export type PaymentRequest = {
  commission_type: string;
  status: PaymentRequestStatus;
  reference: string;
  amount: number;
  urlSuccess: string;
  urlFailed: string;
  urlNotification: string;
  merchant_id: string;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  id: string;
  metadata: {
    payment_processed_date?: Date;
    payment_failed_date?: Date;
    payment_completed_date?: Date;
    payment_failed_reason?: string;
    initial_balance?: number;
    final_balance?: number;
    is_qr?: boolean;
  };
  userInfo?: any;
};

export type PaymentInfo = {
  payment: PaymentRequest;
  merchant: Merchant;
};
export type MerchantContract = {
  status: string;
  _id: string;
  merchant_id: string;
  fees_percentage: string;
  fees_fixed_amount: string;
  max_transaction_limit: number;
  contract_end_date: string;
  contract_start_date: string;
  createdAt: string;
  updatedAt: string;
  id: string;
};

export type Merchant = {
  account: {
    accountNumber: string;
    currency: Currency;
  };
  merchant_id: string;
  status: string;
  name: string;
  email: string;
  phone: string;
  profilePicture?: string;
  address: string;
  api_key: string;
  createdAt: string;
  updatedAt: string;
  contract: MerchantContract;
};

export type GetPaymentResponse = {
  payment: PaymentRequest;
  merchant: Merchant;
};

export type GetQrPaymentResponse = {
  concept: string;
  amount: number;
  accountNumber: string;
  singleUse: boolean;
  user: { email: string };
  customer: { first_name: string; last_name: string };
  bankAccount: { accountNumber: string; currencySymbol: string; currencyCode: string };
  createdAt: string;
};

export type TransferData = {
  amount: number;
  concept: string;
  sendtoAccountId: string;
};

export type PaginationInput = {
  limit: number;
  page: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
};

export type SendOtpCodeResult = {
  success: boolean;
  message: string;
};

export enum MovementType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  FEE = 'FEE'
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  REVERSED = 'REVERSED'
}

export type Transaction = {
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  deletedBy: string | null;
  deletedAt: string | null;
  _deleted: boolean;
  tenantId: string;
  transactionCode: string;
  amount: number;
  status: TransactionStatus;
  timestamp: string;
  feeAmount: number;
  feePercentage: string;
  processedDate: string | null;
  failedDate: string | null;
  completedDate: string | null;
  transactionDetails: any | null;
  rateAppliedSourceAccount: number | null;
  rateAppliedTargetAccount: number | null;
  externalUserId: string | null;
  sourceAccountNumber: string | null;
  targetAccountNumber: string | null;
  sourceAccountCurrencyCode: string | null;
  targetAccountCurrencyCode: string | null;
  transactionTypeId: number;
  commissionRuleIds: number[];
  reference: string;
  isFromBatch: boolean;
  batchId: string | null;
  isReversal: boolean;
  isLegacy: boolean;
  legacyTransfer: boolean;
  invoiceUrl: string | null;
  metadata?: Record<string, any> | null;
};

export type TransactionNote = {
  _id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  accountNumber: string;
  transactionCode: string;
};

export type Movement = {
  id: number;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  type: MovementType;
  balanceBefore: number;
  balanceAfter: number;
  availableBalanceBefore: number | null;
  availableBalanceAfter: number | null;
  code: string;
  amount: number;
  timestamp: string;
  description: string | null;
  accountNumber: string;
  transactionCode: string;
  transaction: Transaction;
  notes?: TransactionNote[];
  notesCount?: number;
};

export enum ExtractionRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

export enum TransactionTypeEnum {
  BILL_PAYMENT = 'BILL_PAYMENT',
  BILL_PAYMENT_FEE = 'BILL_PAYMENT_FEE',
  PAYMENT_EPCARD = 'PAYMENT_EPCARD',
  PAYMENT_TROPIPAY = 'PAYMENT_TROPIPAY',
  PAYMENT_MERCHANT = 'PAYMENT_MERCHANT',
  BANK_TRANSFER = 'BANK_TRANSFER',
  OWNER_TRANSFER = 'OWNER_TRANSFER',
  INTERNAL_TRANSFER = 'INTERNAL_TRANSFER',
  PAYLINK_TRANSFER = 'PAYLINK_TRANSFER',
  QR_TRANSFER = 'QR_TRANSFER'
}
